<script setup lang="ts">
import { TaskList } from './';
import { DragModel } from '../../tir-pm-kanban';
import { computed, onMounted, PropType, ref } from 'vue';
import { TaskModel } from '../../tasks-kanban/models/TaskModel';
import { TasksKanbanGrouppingTypeEnum, TaskViewElementEnum } from '../../tasks-kanban/enums';
import { ListItemModel } from '../models';
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmPagination,
} from 'tir-components';
import { TaskKanbanAdapterService } from '@/components/tasks-kanban/adapter';
import {
  GetTaskListRequestSortModel,
  GroupedTaskItemModel,
  GroupedTaskRequestModel,
  PageRequestModel,
} from '@/components/tasks-kanban/models';
import { useRoute } from 'vue-router';
import { PaginationRequestModel } from '@/models';
import GetAccessCurrentUserInQueueTaskByIdResponseModel from '../models/GetAccessCurrentUserInQueueTaskByIdResponseModel';

/**Параметры */
const props = defineProps({
  /**Задачи */
  tasks: { type: Array as PropType<TaskModel[]> },
  /** список тасков */
  group: { type: Object as PropType<GroupedTaskItemModel> },
  /**Тип группировки */
  grouppingType: { type: Number as PropType<TasksKanbanGrouppingTypeEnum> },
  /**Уровень вложенности */
  deepLevel: { type: Number, default: 0 },
  /**Размер имени задачи */
  nameWidth: { type: String },
  /**Размеры колонок списка */
  listColumns: { type: Array as PropType<ListItemModel[]> },
  /**Проверка кол-ва тасков */
  checkCountTasks: { type: Number as PropType<number> },
  /**Видимость данных группировки в карточке */
  visibleTaskGroupData: { type: Boolean, default: false },
  /**С какого уровня будут видны данные группировки в карточке */
  visibleTaskGroupDataStartLevel: { type: Number, default: 0 },
  /**Доступы юзера к очереди */
  userAccess: { type: Object as PropType<GetAccessCurrentUserInQueueTaskByIdResponseModel> },
  /**Критерии сортировки */
  sortingСriteria: { type: Object as PropType<GetTaskListRequestSortModel> },
});
/**События компонента */
const emit = defineEmits<{
  /**Перетаскивание элемента */
  (e: 'dragComponentOnDrag', isDrag: boolean, data: DragModel<any>): void;
  /**Наведение на элемент */
  (e: 'dragComponentOnOver', isOver: boolean, data: DragModel<any>): void;
  /**Дроп элемента */
  (e: 'dragComponentOnDrop', dropArea: DragModel<any>): void;
  /**Загрузка подзадач */
  (e: 'loadSubtasks', task: TaskModel): void;
  /**Открыть задачу */
  (e: 'open', taskId: number): void;
  /**Удалить задачу */
  (e: 'removeTask', taskId: number);
  /**Увеличить кол-во тасков */
  (e: 'changeCountTasks', num: number);
}>();

/**API сервис */
const apiService = new TaskKanbanAdapterService();

/**Роут */
const route = useRoute();

/**Дефолтное кол-во элементов на странице */
const DEFAULT_PAGE_STEP = 10;

/**Кол-во элементов на странице */
const pageStep = ref(DEFAULT_PAGE_STEP);

/**Текущая страница */
const currentPage = ref<number>(1);

/** данные для отображения при перетаскивании */
const data = computed(() => {
  const isDrag = [
    TasksKanbanGrouppingTypeEnum.Status,
    TasksKanbanGrouppingTypeEnum.Priority,
    TasksKanbanGrouppingTypeEnum.Type,
  ].includes(props.grouppingType);

  return props.tasks?.map(
    (task) =>
      new DragModel({
        Data: task,
        AllowDrag: false,
        AllowDrop: true,
        IsDragging: false,
      }),
  );
});
/**Id текущей группы */
const groupId = computed(() => {
  switch (props.grouppingType) {
    case TasksKanbanGrouppingTypeEnum.Status:
      return props.group?.Status.Id;
    case TasksKanbanGrouppingTypeEnum.Performer:
      return props.group?.Performer.Id;
    case TasksKanbanGrouppingTypeEnum.Author:
      return props.group?.Author.Id;
    case TasksKanbanGrouppingTypeEnum.Priority:
      return props.group?.Priority.Id;
    case TasksKanbanGrouppingTypeEnum.Tag:
      return props.group?.Tag.Id;
    case TasksKanbanGrouppingTypeEnum.Type:
      return props.group?.Type.Id;
  }
});

/**Кол-во страниц */
const pagesCount = computed(() => {
  return Math.ceil(props.group?.Count / pageStep.value);
});

/**Заголовки списка */
const headers = computed(() => {
  return props.listColumns.map((x, i) => ({
    Name: x.Name,
    Style: {
      width: props.listColumns[i].Width,
      display:
        x.Type === TaskViewElementEnum.Status ||
        TaskViewElementEnum.Priority ||
        TaskViewElementEnum.WorkTime ||
        TaskViewElementEnum.EstimateTime
          ? 'flex'
          : 'block',
      justifyContent:
        x.Type === TaskViewElementEnum.Status
          ? 'flex-end'
          : x.Type === TaskViewElementEnum.Priority
          ? 'center'
          : x.Type === TaskViewElementEnum.EstimateTime
          ? 'flex-end'
          : x.Type === TaskViewElementEnum.WorkTime
          ? 'flex-end'
          : 'unset',
    },
  }));
});

/**Отображаемые элементы */
const viewElements = ref<Array<TaskViewElementEnum>>(Object.values(TaskViewElementEnum));

// /**Получить список задач группировок */
const getListTasksGroups = async (page?: PaginationRequestModel) => {
  const queueId = route.params.queueId;

  const response = await apiService.getTaskList(
    new GroupedTaskRequestModel({
      ParameterId: groupId.value,
      GrouppedBy: props.grouppingType,
      QueueTaskId: String(queueId),
      Sort: props.sortingСriteria,
      ViewSettings: {
        showNestedTasks: false,
        viewElements: viewElements.value,
      },
      Page: new PageRequestModel({
        Skip: page.Skip,
        Take: page.Take,
      }),
    } as any),
  );
  if (response.IsSuccess) {
    props.group.Items = response.Items;
  }
};

/**Изменить кол-во элементов на странице */
const changePageStep = async (value: number) => {
  pageStep.value = value;
  await getListTasksGroups(
    new PaginationRequestModel({
      Skip: 0,
      Take: pageStep.value,
    }),
  );
};

const changeCountTasks = () => {
  emit('changeCountTasks', 10);
};

/**Обновить текущую страницу */
const changeCurrentPage = async (value: number) => {
  currentPage.value = value;
  if (!pageStep.value) return;

  await getListTasksGroups(
    new PaginationRequestModel({
      Skip: pageStep.value * (value - 1),
      Take: pageStep.value,
    }),
  );
};

/** элемент над которым перетаскивается */
const dragComponentOnOver = (isOver: boolean, model: DragModel<TaskModel>) => {
  emit('dragComponentOnOver', isOver, model);
};

/** перетаскивание элемента */
const dragComponentOnDrag = (isDragging: boolean, model: DragModel<TaskModel>) => {
  emit('dragComponentOnDrag', isDragging, model);
};

/** сброс узла с тасками */
const dragComponentOnDrop = (model: DragModel<TaskModel>) => {
  emit('dragComponentOnDrop', model);
};

/**Стили элемента заголовка */
// const headersStyle = computed(() => ({ left: `calc(${props.nameWidth} + 2.5rem)` }));
</script>
<template>
  <TaskList
    class="tasks-list-wrapper_block"
    v-for="(item, index) in data"
    :key="index"
    :userAccess="userAccess"
    :task="item"
    :group="group"
    :groupping-type="grouppingType"
    :deepLevel="deepLevel"
    :nameWidth="nameWidth"
    :listColumns="listColumns"
    :visible-task-group-data="visibleTaskGroupData"
    :visible-task-group-data-start-level="visibleTaskGroupDataStartLevel"
    @open="emit('open', $event)"
    @removeTask="emit('removeTask', $event)"
    @dragComponentOnDrag="dragComponentOnDrag"
    @dragComponentOnOver="dragComponentOnOver"
    @dragComponentOnDrop="dragComponentOnDrop"
    @loadSubtasks="emit('loadSubtasks', $event)"
  />
  <TirPmPagination
    v-if="
      group?.Count > 10 && props.grouppingType !== TasksKanbanGrouppingTypeEnum.None && pagesCount
    "
    class="tasks-list-wrapper__paginator"
    showMoreElements
    :pages="pagesCount"
    :range-size="3"
    :model-value="currentPage"
    :options="[10, 20, 30, 40, 50, 100]"
    :count-elements="pageStep"
    @update:count-elements="changePageStep"
    @update:model-value="changeCurrentPage"
  />
  <div
    v-if="group?.Count > 10 && props.grouppingType !== TasksKanbanGrouppingTypeEnum.None"
    class="tasks-list-wrapper__line"
  />
</template>
<style lang="scss" scoped>
.empty-state-block {
  display: flex;
  justify-content: center;
}

.tasks-list-wrapper__line {
  background-color: var(--neutral-30);
  height: 0.0625rem;
}

.tasks-list-wrapper__paginator {
  margin-top: 0.875rem;
}

.tasks-list-wrapper_block {
  position: relative;
  background-color: var(--neutral-translucent-0);
}
.tir-pm-task-group_headers {
  margin-left: 4.3125rem;
  position: absolute;
  top: 4rem;
  transform: translateY(-50%);
  display: flex;
  color: var(--text-tertiary);
  font-size: 0.75rem;
}
</style>
<style lang="scss">
.tasks-list-wrapper__paginator .pagination {
  position: sticky;
  right: 0;
}

.tasks-list-wrapper__paginator .show-more-elements {
  position: sticky;
  left: 0;
}
</style>
